<template>
    <div class="d-flex-grow justify-center">
         <v-form @submit.prevent="store" class="w-100 w-md-50rem max-w-100">
             <v-card class="d-flex-col-grow">
                 <v-card-text class="flex-grow-1">
                     <div class="d-flex flex-column ga-7">
                         <div class="fs-2 fw-medium">
                             Payment Information
                         </div>
                         <div v-if="page.props.data?.bySuperadmin" class="mt-n3 mb-n2 fs-1 fw-medium">
                            <div>Name: {{ page.props.data?.admin?.name }}</div>
                            <div>Email: {{ page.props.data?.admin?.email }}</div>
                         </div>
                         <div v-if="form.errors.custom_error" class="fs-1 text-error">
                             {{ form.errors.custom_error }}
                         </div>
                         <div class="d-none">
                             <v-text-field-v1
                                 label="Identifier"
                                 v-model="form.name"
                                 placeholder=""
                                 required
                                 :error-messages="form.errors.name"
                             ></v-text-field-v1>
                         </div>
                         <div>
                            <v-row>
                                <v-col cols="12">
                                     <div>
                                         <v-text-field-v1
                                             label="Amount"
                                             v-model="form.amount"
                                             placeholder="0.00"
                                             @change="(e: any) => {form.amount = helpers.numberFormatter(e.target.value, 8)}"
                                             :error-messages="form.errors.amount"
                                         >
                                            <template #append-inner>
                                                <span>USD</span>
                                            </template>
                                        </v-text-field-v1>
                                        <div class="text-note pt-3">
                                            You can make request for maximum <span class="fw-bold pe-4">{{ helpers.moneyFormatNew(page.props.data?.requestableAmount, 2, true) }}</span>
                                            <v-btn color="primary" density="compact" variant="tonal" 
                                                    class="text-none px-2 py-1 h-auto"
                                                    @click.prevent="() => { form.amount = helpers.numberFormatter(page.props.data?.requestableAmount, 8) }"
                                                    >
                                                Add All
                                            </v-btn>
                                        </div>
                                     </div>
                                 </v-col>
                                <v-col cols="12">
                                     <div>
                                         <v-select-v1
                                             label="Method"
                                             v-model="form.payment_method"
                                             :items="Object.values(page.props.data?.paymentMethods ?? {})"
                                             item-title="name"
                                             item-value="key"
                                             :error-messages="form.errors.payment_method"
                                         ></v-select-v1>
                                     </div>
                                 </v-col>
                                
                                <v-col cols="12" md="5" v-if="form.payment_method == 'crypto'">
                                     <div>
                                         <v-text-field-v1
                                             label="Coin"
                                             v-model="form.additional_data.coin"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                                <v-col   v-if="form.payment_method == 'crypto' || form.payment_method == 'usdt'">
                                     <div>
                                         <v-text-field-v1
                                             label="Wallet Address"
                                             v-model="form.additional_data.address"
                                         ></v-text-field-v1>
                                     </div>
                                 </v-col>
                                <v-col cols="12" v-if="form.payment_method == 'wire'">
                                     <div>
                                         <v-textarea-v1
                                             label="Wire Info"
                                             rows="3"
                                             v-model="form.additional_data.wireInfo"
                                         ></v-textarea-v1>
                                     </div>
                                </v-col>
                                <v-col cols="12" v-if="form.errors.additional_data">
                                    <div class="text-error">{{ form.errors.additional_data }}</div>
                                </v-col>
                                <v-col cols="12">
                                     <div>
                                         <v-textarea-v1
                                             label="Comments"
                                             v-model="form.comments"
                                             rows="5"
                                             :error-messages="form.errors.comments"
                                         ></v-textarea-v1>
                                     </div>
                                 </v-col>
                            </v-row>
                         </div>
                         <div v-if="form.errors.custom_error" class="fs-1 pb-2 text-error">
                             {{ form.errors.custom_error }}
                         </div>
                     </div>
                 </v-card-text>
                 <v-card-actions-form>
                     <div class="d-flex flex-grow-1 justify-space-between">
                         <v-btn-input-compact color="btn-secondary" @click="helpers.goBack">
                             <template #prepend>
                                 <mdi-icon>chevron_left</mdi-icon>
                             </template>
                             Back
                         </v-btn-input-compact>
                         <v-btn-input-compact type="submit" :disabled="form.processing" :loading="form.processing">
                             <template #prepend>
                                 <mdi-icon>check</mdi-icon>
                             </template>
                             Save
                         </v-btn-input-compact>
                     </div>
                     
                 </v-card-actions-form>
             </v-card>
         </v-form>
     </div>
 </template>
 <script setup lang="ts">
     import { ref } from "vue";
     import { useForm, usePage, router } from '@inertiajs/vue3';
     import { toast } from 'vue3-toastify';
     import { SharedPage, SharedPageProps } from '@/Types/inertia';
     import { User } from '@/Types/Models/user';
     import helpers from '@/Plugins/helpers';
     import { pageTitle, containerGapForForm } from '@/Composables/PageMeta';
     import { Company } from "@/Types/Models/company";
     import { PaymentRequest, PaymentRequest_Method_Map } from "@/Types/Models/payment-request";
     import { TrackBalance } from "@/Types/Models/track-balance";
     import { useDate } from 'vuetify';
     import { useAuth } from '@/Composables/Auth';

 
     pageTitle.value = 'Create Request';
     containerGapForForm();
     interface PageData extends SharedPageProps{
         data?:{
            paymentRequest?: PaymentRequest,
            paymentMethods?: PaymentRequest_Method_Map,
            requestableAmount?: string,
            success_redirect?: string,
            bySuperadmin: boolean,
            admin: User,
            submissionToken?: string,
         }
     }

     interface Form extends PaymentRequest{
         custom_error: string,
         success_redirect: string,
         admin_id: number | null,

         additional_data: {
            address: string | null,
            coin: string | null,
            wireInfo: string | null,
         }
     }
 
     const page = usePage<PageData>();
     const paymentRequest = ref<PaymentRequest>(page.props.data?.paymentRequest ?? {} as PaymentRequest);
     if(paymentRequest.value?.id){
        pageTitle.value = 'Update Request';
     }
     const form = useForm<Form>({
         custom_error: '',
         id: paymentRequest.value?.id ?? 0,
         idempotency_key: paymentRequest.value?.id ? null : (page.props.data?.submissionToken ?? ''),
         admin_id: page.props.data?.admin.id ?? 0,
         name: paymentRequest.value?.name ?? '',
         amount: helpers.numberFormatter(paymentRequest.value.amount ?? 0, 8),
         payment_method: paymentRequest.value.payment_method ?? null,
         additional_data: {
            address: paymentRequest.value.additional_data?.address ?? null,
            coin: paymentRequest.value.additional_data?.coin ?? null,
            wireInfo: paymentRequest.value.additional_data?.wireInfo ?? null,
         },
         comments: paymentRequest.value.comments ?? null,
         
         success_redirect: page.props.data?.success_redirect ?? '',
     });

 
     const store = async () => {
            form.clearErrors();
            const _route = page.props.data?.bySuperadmin ? 'paymentRequests.superadmin.admin.store' : 'paymentRequests.store';
            form.post(route(_route, paymentRequest.value?.id ?? 0), {
                 preserveState: true,
                 onSuccess: (page: SharedPage) => {
                     toast.success(page.props?.jetstream?.flash?.msg?.text ?? 'Payment request is saved');
                     form.clearErrors();
                 },
                 onFinish: (visit) => {
                     // console.log('visit onFinish', visit);
                 },
                 onError: (errors) => {
                     console.log('onError', errors);
                 }
            });
     }
 </script>
